import React from "react";
import { Link } from "react-router-dom";
import { Youtube, ArrowUpRight, PlayCircle } from "lucide-react";
import Reveal from "../components/common/Reveal";

const CATEGORIES = [
  { icon: "✈️", title: "Escape Inspiration", body: "Dream-worthy destinations, bucket list ideas, and places you didn't even know you needed to visit." },
  { icon: "🧳", title: "Smart Travel Tips", body: "Insider advice to make your escape smoother — from planning to packing to navigating travel days." },
  { icon: "🏝️", title: "Real Experiences", body: "Honest insights from my own travels — what's worth it, what to skip, and what truly makes a destination unforgettable." },
  { icon: "🥂", title: "Luxury & Hidden Gems", body: "Because the best escapes aren't always the most obvious ones." },
];

export default function Blog() {
  return (
    <div data-testid="page-blog">
      <section className="py-24 lg:py-36 px-6 lg:px-10 bg-ivory">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="star-sep mb-8">The Escape Edit</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98]" data-testid="blog-title">
              Where inspiration meets<br /> your <span className="italic text-gold">next escape.</span>
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={120}>
            <p className="text-lg text-muted leading-relaxed font-light">
              Travel inspiration, expert tips, destination highlights, and behind-the-scenes insights from a Chief Escape Artist who truly lives it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Coming soon feature */}
      <section className="py-16 lg:py-24 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="relative aspect-video bg-nightsky text-ivory flex items-center justify-center overflow-hidden" data-testid="blog-coming-soon-card">
              <img
                src="https://images.unsplash.com/photo-1511692760-3b6e8bbba3fe?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
                alt="Coastal coming soon"
                className="absolute inset-0 w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              <div className="relative text-center px-6">
                <PlayCircle size={72} strokeWidth={0.75} className="mx-auto text-gold mb-6 animate-shimmer" />
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Coming Soon</div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
                  Videos are <span className="italic">brewing.</span>
                </h2>
                <p className="mt-6 max-w-xl mx-auto text-ivory/70 text-base font-light leading-relaxed">
                  New videos, destination features, and insider stories are on the way. Subscribe on YouTube to be the first to watch.
                </p>
                <a
                  href="https://www.youtube.com/@LuxeTiniEscapes"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex items-center gap-3 h-12 px-8 bg-gold text-ink hover:bg-goldDeep transition-colors text-[12px] uppercase tracking-[0.28em]"
                  data-testid="blog-youtube-cta"
                >
                  <Youtube size={16} strokeWidth={1.5} /> Subscribe @LuxeTiniEscapes
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What you'll find */}
      <section className="py-24 lg:py-32 px-6 lg:px-10">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="star-sep mb-8"><span>What's Coming</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light max-w-3xl leading-tight mb-14">
              What you'll find here.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="border-t border-gold pt-8 h-full">
                  <div className="text-3xl mb-5">{c.icon}</div>
                  <div className="font-serif text-2xl text-ink">{c.title}</div>
                  <p className="mt-3 text-sm text-muted font-light leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 px-6 lg:px-10 bg-[#F3EDE1]">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="star-sep justify-center mb-8"><span>From Dreaming</span></div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              From dreaming to <span className="italic text-gold">departing.</span>
            </h2>
            <p className="mt-8 text-lg text-muted font-light leading-relaxed">
              Watching is just the beginning. If something sparks your interest, I can take it from inspiration to a fully designed, stress-free escape tailored just for you.
            </p>
            <Link
              to="/plan-your-escape"
              className="mt-10 inline-flex items-center gap-3 h-14 px-10 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em]"
              data-testid="blog-bottom-cta"
            >
              Start Planning Your Escape <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
